import { ref, type Ref } from "vue";
import { ApolloQueryResult } from "@apollo/client/core";
import { generateApolloClient } from "@/common/apolloClientUtil";
import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetShopCode,
} from "@/common/localStorageUtil";
import {
  ListDeliveryAvailabilityQuery,
  ListDeliveryAvailabilityDocument,
  ListDeliveryAvailabilityQueryVariables,
} from "@/generated/graphql";
import type { DeliveryAvailabilityData } from "../types";
import { WEEKS } from "../constants";

interface UseRegularDeliveryAvailabilityParams {
  btnDisabled: Ref<boolean>;
}

/**
 * 配送可否の判定ロジックを提供する composable。
 *
 * @returns 画面ロジックで利用する状態・関数
 */
export const useRegularDeliveryAvailability = ({
  btnDisabled,
}: UseRegularDeliveryAvailabilityParams) => {
  // 便選択のタイトル
  const deliveryAvailabilityHeader = ref<Map<string, string>>(new Map());
  // 便選択の中身
  const deliveryAvailabilityData = ref<DeliveryAvailabilityData[][]>([]);
  // Store用の便の中身
  const deliveryAvailabilityStore = ref<DeliveryAvailabilityData[][]>([]);

  const onDeliveryAvailabilityClick = async (
    date: string,
    deliveryid: string
  ): Promise<void> => {
    console.log(date + "/" + deliveryid);
  };

  const showDeliveryAvailability = async (
    area_code: string | null
  ): Promise<void> => {
    const apolloClient = await generateApolloClient();

    deliveryAvailabilityData.value = [];
    deliveryAvailabilityHeader.value = new Map();
    //  配達時間一覧取得
    const result: ApolloQueryResult<ListDeliveryAvailabilityQuery> =
      await apolloClient.query<
        ListDeliveryAvailabilityQuery,
        ListDeliveryAvailabilityQueryVariables
      >({
        query: ListDeliveryAvailabilityDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          shop_code: localStorageGetShopCode(),
          area_code: area_code ? area_code : "",
        },
        fetchPolicy: "no-cache",
      });

    const list = result.data.listDeliveryAvailability;
    if (list === undefined || list === null) {
      return;
    }

    // 選択されているエリア
    let defaultAreaCode = "";
    let currentDate = "";
    let currentAvialibity: DeliveryAvailabilityData[] = [];
    let idx = 0;
    list.forEach((value) => {
      if (value !== undefined && value !== null) {
        if (defaultAreaCode.length == 0) {
          defaultAreaCode = value.area_code;
        }
        if (value.area_code !== defaultAreaCode) {
          return;
        }
        // 日付をYYYY-MM-DDに変更する
        value.delivery_date = value.delivery_date.substring(0, 10);
        if (currentDate.length == 0 || currentDate != value.delivery_date) {
          currentDate = value.delivery_date;
          if (currentAvialibity.length > 0) {
            deliveryAvailabilityData.value.push(currentAvialibity);
          }
          currentAvialibity = [];
          idx = 0;
        }

        // 配達日ごとの配達数の列
        currentAvialibity.push({
          key: ++idx,
          delivery_id: value.delivery_id,
          delivery_id_desc: value.delivery_id_desc,
          delivery_date: value.delivery_date,
          availability: value.availability.trim(),
          area_code: value.area_code,
          delivery_start_time: value.delivery_start_time,
          delivery_end_time: value.delivery_end_time,
        });
        // 配達便の列
        deliveryAvailabilityHeader.value.set(
          value.delivery_id_desc,
          value.delivery_id_desc
        );
      }
    });
    if (currentAvialibity.length > 0) {
      deliveryAvailabilityData.value.push(currentAvialibity);
    }
    /*
    if (defaultAreaCode !== "") {
      this.areaSelectValues.area_code = defaultAreaCode;
      this.areaSelectValues.area_name = defaultAreaName;
    }
    */
  };

  /**
   * 配達便取得
   */
  const getDeliveryAvailable = async (): Promise<void> => {
    //  配達時間一覧取得
    const apolloClient = await generateApolloClient();
    const resultAvailability: ApolloQueryResult<ListDeliveryAvailabilityQuery> =
      await apolloClient.query<
        ListDeliveryAvailabilityQuery,
        ListDeliveryAvailabilityQueryVariables
      >({
        query: ListDeliveryAvailabilityDocument,
        variables: {
          customer_code: localStorageGetCustomerCode(),
          chain_code: localStorageGetChainCode(),
          shop_code: localStorageGetShopCode(),
        },
        fetchPolicy: "no-cache",
      });

    const list = resultAvailability.data.listDeliveryAvailability;
    if (list === undefined || list === null) {
      return;
    }

    // 選択されているエリア
    let currentDate = "";
    const allAvialibity: DeliveryAvailabilityData[] = [];
    let idx = 0;
    list.forEach((value) => {
      if (value !== undefined && value !== null) {
        // 日付をYYYY-MM-DDに変更する
        value.delivery_date = value.delivery_date.substring(0, 10);
        if (currentDate.length == 0 || currentDate != value.delivery_date) {
          currentDate = value.delivery_date;
          idx = 0;
        }

        // 配達日ごとの配達数の列
        allAvialibity.push({
          key: ++idx,
          delivery_id: value.delivery_id,
          delivery_id_desc: value.delivery_id_desc,
          delivery_date: value.delivery_date,
          availability: value.availability.trim(),
          area_code: value.area_code,
          delivery_start_time: value.delivery_start_time,
          delivery_end_time: value.delivery_end_time,
        });
      }
    });

    deliveryAvailabilityStore.value = [];
    deliveryAvailabilityStore.value.push(allAvialibity);
    btnDisabled.value = false;
  };

  /**
   * 曜日変換
   */
  const formatDate = (date: string): string => {
    let _date: Date | undefined;
    let retDate = "";
    if (date) {
      _date = new Date(date);
      retDate =
        _date.getMonth() +
        1 +
        "/" +
        _date.getDate() +
        "(" +
        WEEKS[_date.getDay()] +
        ")";
    }
    return retDate;
  };

  return {
    deliveryAvailabilityHeader,
    deliveryAvailabilityData,
    deliveryAvailabilityStore,
    onDeliveryAvailabilityClick,
    showDeliveryAvailability,
    getDeliveryAvailable,
    formatDate,
  };
};
